<template>
  <v-dialog v-model="dialog" fullscreen hide-overlay>
    <v-card class="hist">
      <ErrorAlert :message="errorMessage" @clear-error="clearMessage" />
      <SuccessAlert :message="successMessage" @clear-error="clearMessage" />
      <div class="fdlg__form hist__form">
        <div class="fdlg__head">
          <div>
            <h2 class="fdlg__title">{{ approach_number ? `Demersul nr. ${approach_number}` : "Demers fără număr încă" }}</h2>
            <p class="fdlg__sub">{{ [cause, equipment, model].filter(Boolean).join(", ") }}</p>
          </div>
          <v-btn icon aria-label="Închide" @click="handleClose()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="fdlg__body">
          <div class="hist__inner">
            <div v-if="isCancelled" class="notice notice--brick">
              <v-icon>mdi-cancel</v-icon>
              <span>
                <strong>Demersul a fost anulat</strong>
                <template v-if="cancelStep"> de {{ cancelStep.user }}, {{ cancelStep.date }}</template>.
                <template v-if="cancelStep && cancelStep.comments"><br />Motivul: {{ cancelStep.comments }}</template>
              </span>
            </div>
            <div v-else-if="isCorrected" class="notice notice--sky">
              <v-icon>mdi-account-edit-outline</v-icon>
              <span>
                <strong>Corectat de utilizator.</strong> După respingerea Contabilității, autorul a corectat demersul;
                acum așteaptă din nou decizia șefului departamentului.
              </span>
            </div>

            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-file-document-outline</v-icon>Demers</div>
              <dl class="summary">
                <div>
                  <dt>Număr demers</dt>
                  <dd>{{ approach_number || "încă nu are" }}</dd>
                </div>
                <div>
                  <dt>Data emiterii</dt>
                  <dd>{{ created_at }}</dd>
                </div>
                <div>
                  <dt>Starea curentă</dt>
                  <dd><StatusChip :status="status" /></dd>
                </div>
                <div>
                  <dt>Cauza</dt>
                  <dd>{{ cause || "—" }}</dd>
                </div>
              </dl>
            </section>

            <div class="facts-grid">
              <section class="form-section">
                <div class="form-section__label"><v-icon small>mdi-account-outline</v-icon>Utilizator</div>
                <dl class="facts">
                  <dt>Utilizatorul</dt>
                  <dd>{{ user }}</dd>
                  <dt>Departament</dt>
                  <dd>{{ userDepartment || "—" }}</dd>
                  <dt>Telefon mobil</dt>
                  <dd>{{ telephone }}</dd>
                  <dt>Telefon fix</dt>
                  <dd>{{ landline || "—" }}</dd>
                </dl>
              </section>
              <section class="form-section">
                <div class="form-section__label"><v-icon small>mdi-desktop-classic</v-icon>Echipament</div>
                <div v-if="createsEquipment" class="new-item">
                  <v-icon small>mdi-new-box</v-icon>Echipament nou (cerut prin demers)
                </div>
                <dl class="facts">
                  <dt>Tip</dt>
                  <dd>{{ equipment }}</dd>
                  <dt>Model</dt>
                  <dd>{{ model || "—" }}</dd>
                  <dt>Nr. inventar</dt>
                  <dd>{{ registration_number || "—" }}</dd>
                  <dt>Departament</dt>
                  <dd>{{ equipmentdepartment || "—" }}</dd>
                  <dt>Locația</dt>
                  <dd>{{ location || "—" }}</dd>
                </dl>
                <div v-if="canEditItem" class="item-edit">
                  <v-btn outlined color="primary" @click="$refs.itemEdit.open()">
                    <v-icon left>mdi-pencil-outline</v-icon>Editează datele echipamentului
                  </v-btn>
                  <p class="item-edit__hint">Se salvează în istoricul demersului («Date modificate»); demersul nu își schimbă etapa.</p>
                </div>
              </section>
            </div>

            <section v-if="canFinish || canEditPrice" class="form-section">
              <div class="form-section__label"><v-icon small>mdi-wrench-outline</v-icon>Îndeplinirea demersului</div>
              <p class="form-section__note">
                {{ canFinish
                  ? "Prețul și comentariul se salvează când marcați demersul ca îndeplinit."
                  : "Demersul este îndeplinit. Puteți corecta prețul deservirii." }}
              </p>
              <v-row dense>
                <v-col cols="12" sm="5" md="4">
                  <v-text-field
                    v-model="price"
                    label="Prețul deservirii"
                    prepend-inner-icon="mdi-cash"
                    suffix="lei"
                    hint="De exemplu 150 sau 150,50"
                    :error-messages="priceError"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-if="canFinish"
                    v-model="body"
                    label="Comentarii"
                    hint="Opțional. Se salvează împreună cu marcarea ca îndeplinit."
                    persistent-hint
                    outlined
                    auto-grow
                    rows="3"
                  ></v-textarea>
                </v-col>
                <v-col v-if="canFinish" cols="12">
                  <PhotoInput
                    v-model="photos"
                    purpose="comment"
                    :max="6"
                    label="Fotografii (opțional)"
                    hint="De exemplu lucrarea făcută; se salvează împreună cu marcarea ca îndeplinit."
                    @uploading="photosUploading = $event"
                    @error="handleError"
                  />
                </v-col>
              </v-row>
            </section>

<section v-if="details && details.circuit" class="form-section">
              <div class="form-section__label"><v-icon small>mdi-sitemap-outline</v-icon>Traseul aprobării</div>
              <p class="form-section__note">Circuitul „{{ details.circuit.name }}”, cu care a fost depus demersul.</p>
              <CircuitPath
                :steps="details.circuit.steps"
                :review="details.circuit.review"
                :engineer-role="details.engineerRole"
                :waiting="details.waitingFor || ''"
                :finished="statusId === 9"
                :examined="!!details.examined"
              />
            </section>

            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-timeline-text-outline</v-icon>Drumul demersului</div>
              <p v-if="timelineLoading" class="form-section__note">Se încarcă etapele...</p>
              <p v-else-if="timeline.length === 0" class="form-section__note">Nu există etape înregistrate.</p>
              <ol v-else class="road">
                <li v-for="step in timeline" :key="step.id" class="road__step" :class="stepClass(step)">
                  <span class="road__dot" aria-hidden="true"></span>
                  <div class="road__body">
                    <div class="road__head">
                      <StatusChip :status="step.status" small />
                      <strong>{{ step.user }}</strong>
                      <span class="road__date">{{ step.date }}</span>
                    </div>
                    <template v-for="(line, index) in commentLines(step)">
                      <ul v-if="line.changes" :key="index" class="road__changes">
                        <li v-for="(change, cIndex) in line.changes" :key="cIndex">
                          <template v-if="change.field">
                            <span class="road__field">{{ change.field }}:</span>
                            <span class="road__from">{{ change.from }}</span>
                            <v-icon x-small>mdi-arrow-right</v-icon>
                            <span class="road__to">{{ change.to }}</span>
                          </template>
                          <template v-else>{{ change.text }}</template>
                        </li>
                      </ul>
                      <div v-else :key="index" class="road__text">{{ line.text }}</div>
                    </template>
                    <PhotoGallery v-if="step.files && step.files.length" class="road__photos" :files="step.files" :size="72" download-name="demers" />
                  </div>
                </li>
              </ol>
              <v-btn v-if="canComment" outlined color="primary" class="road__add" @click="openAddComment">
                <v-icon left>mdi-comment-plus-outline</v-icon>Adaugă comentariu
              </v-btn>
            </section>
          </div>
        </v-card-text>

        <div class="fdlg__foot">
          <v-spacer></v-spacer>
          <v-btn text large @click="handleClose()">Închide</v-btn>
          <v-btn v-if="canFinish" class="btn-green" large :loading="saving" :disabled="photosUploading" @click="showConfirmCard('done')">
            <v-icon left>mdi-check-circle-outline</v-icon>{{ doneStep.label || "Îndeplinit" }}
          </v-btn>
          <v-btn v-else-if="canEditPrice" class="btn-green" large :loading="saving" @click="savePrice">
            <v-icon left>mdi-content-save-outline</v-icon>Salvează prețul
          </v-btn>
        </div>
      </div>

      <v-dialog v-model="confirmCard" persistent max-width="480">
        <v-card class="confirm">
          <div class="confirm__icon" aria-hidden="true"><v-icon>mdi-help-circle-outline</v-icon></div>
          <p class="confirm__title">{{ confirmTitle }}</p>
          <p class="confirm__text">Demersul va fi marcat ca îndeplinit, iar prețul, comentariul și fotografiile vor fi salvate.</p>
          <div class="confirm__actions">
            <v-btn text large @click="cancelConfirmCard">Anulare</v-btn>
            <v-btn class="btn-green" large @click="confirmAction">Da, continuă</v-btn>
          </div>
        </v-card>
      </v-dialog>

      <ItemEditDialog ref="itemEdit" :request="details" @saved="onItemSaved" />
      <AddCommentDialog ref="addComment" @saved="onCommentAdded" @success="handleSuccess" @error="handleError" />
    </v-card>
  </v-dialog>
</template>

<script>
import * as api from "./api";
import { mapGetters } from "vuex";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";
import SuccessAlert from "@/components/alerts/SuccessAlert.vue";
import StatusChip, { statusTone } from "@/components/elements/StatusChip.vue";
import ItemEditDialog from "@/components/requests/ItemEditDialog.vue";
import CircuitPath from "@/components/requests/CircuitPath.vue";
import AddCommentDialog from "@/components/requests/AddCommentDialog.vue";
import PhotoInput from "@/components/files/PhotoInput.vue";
import PhotoGallery from "@/components/files/PhotoGallery.vue";
import { ITEM_EDITOR_ROLES, STAGES_005, STAGES_008 } from "@/config/periferice";
import { parsePrice, PRICE_ERROR } from "@/helper/helper.js";

const DONE = 9;

// Steps whose comment ends with the list of changed fields ("Model: A → B; Locația: X → Y")
const STEPS_WITH_CHANGES = [STAGES_005.CORRECTED, STAGES_005.DATA_CHANGED];

function parseChanges(line) {
  return line.split("; ").map((part) => {
    const colon = part.indexOf(": ");
    const arrow = part.indexOf(" → ", colon);
    if (colon < 1 || arrow < 0) return { text: part };
    return { field: part.slice(0, colon), from: part.slice(colon + 2, arrow), to: part.slice(arrow + 3) };
  });
}

export default {
  name: "HistoryDetails",
  props: ["item"],
  components: {
    ErrorAlert,
    SuccessAlert,
    StatusChip,
    ItemEditDialog,
    CircuitPath,
    AddCommentDialog,
    PhotoInput,
    PhotoGallery,
  },
  data() {
    return {
      dialog: true,
      approach_number: "",
      created_at: "",
      status: "",
      user: "",
      userDepartment: "",
      equipment: "",
      model: "",
      registration_number: "",
      equipmentdepartment: "",
      location: "",
      price: "",
      cause: "",
      body: "",
      // photos saved with "Îndeplinit" (already uploaded)
      photos: [],
      photosUploading: false,
      saving: false,
      errorMessage: "",
      successMessage: "",
      confirmCard: false,
      confirmTitle: "",
      pendingAction: null,
      telephone: "",
      landline: "",
      // what the server lets the caller do now ([{ id, label, tone }]) and the request as filed
      steps: [],
      details: null,
      timeline: [],
      timelineLoading: false,
      loadToken: 0,
    };
  },
  methods: {
    async loadRequest() {
      if (!this.item) return;
      const id = this.item.id;
      const token = ++this.loadToken;
      this.timelineLoading = true;
      const [steps, details, timeline] = await Promise.allSettled([
        api.getNextSteps(id),
        api.getRequestDetails(id),
        api.getRequestTimelineData(id),
      ]);
      if (token !== this.loadToken) return;

      this.steps = steps.status === "fulfilled" ? steps.value || [] : [];
      const row = details.status === "fulfilled" && Array.isArray(details.value) ? details.value[0] : null;
      this.details = row || null;
      if (row) {
        // the request's own copy of the item, fresh after an edit
        this.model = row.model;
        this.registration_number = row.registration_number;
        this.location = row.equipmentLocation;
      }
      this.timeline = timeline.status === "fulfilled"
        ? [...(timeline.value || [])].sort((a, b) => a.id - b.id)
        : [];
      this.timelineLoading = false;
      [steps, details, timeline]
        .filter((result) => result.status === "rejected")
        .forEach((result) => console.error("Error loading request details:", result.reason));
    },
    // "Îndeplinit" is offered by the server (the request's engineer, once); a request that is done only gets its price corrected
    async done() {
      if (!this.canFinish || this.saving || this.photosUploading || this.priceError) return;
      this.saving = true;
      try {
        // "Îndeplinit" and the price are saved together
        await api.saveResponseData({
          response_type_id: this.doneStep.id,
          request_id: this.item.id,
          comments: this.body,
          file_ids: this.photos.map((photo) => photo.id),
          service_price: this.price === null || this.price === undefined ? "" : String(this.price),
        });
        this.$emit("updateRequestData");
        this.handleSuccess("Demersul a fost marcat ca îndeplinit");
        setTimeout(this.handleClose, 900);
      } catch (error) {
        console.error("Error saving request:", error);
        this.handleError(error.response?.data?.message || "Demersul nu a putut fi marcat ca îndeplinit");
      } finally {
        this.saving = false;
      }
    },
    async savePrice() {
      if (!this.canEditPrice || this.saving || this.priceError) return;
      this.saving = true;
      try {
        const price = parsePrice(this.price);
        await api.updateRequests({ id: this.item.id, service_price: price === null ? 0 : price });
        this.$emit("updateRequestData");
        this.handleSuccess("Prețul a fost salvat");
        setTimeout(this.handleClose, 900);
      } catch (error) {
        console.error("Error saving price:", error);
        this.handleError(error.response?.data?.message || "Prețul nu a fost salvat. Încercați din nou.");
      } finally {
        this.saving = false;
      }
    },
    onItemSaved(message) {
      this.handleSuccess(message);
      this.$emit("updateRequestData");
      this.loadRequest();
    },
    openAddComment() {
      const what = [this.cause, this.model].filter(Boolean).join(", ");
      this.$refs.addComment.open(this.item.id, what ? `${what}. Demersul nu își schimbă etapa.` : "");
    },
    onCommentAdded() {
      this.$emit("updateRequestData");
      this.loadRequest();
    },
    stepClass(step) {
      if (step.statusId === STAGES_005.CANCELLED) return "is-cancelled";
      if (step.statusId === STAGES_005.CORRECTED) return "is-corrected";
      if (step.statusId === STAGES_005.DATA_CHANGED) return "is-changed";
      if (step.statusId === STAGES_008.COMMENT) return "is-comment";
      return "tone-" + statusTone(step.status);
    },
    // the comment as lines; for corrections and item edits the last line lists the changed fields
    commentLines(step) {
      const lines = (step.comments || "").split("\n").filter((line) => line.trim());
      return lines.map((line, index) => {
        const isChangeLine = STEPS_WITH_CHANGES.includes(step.statusId) && index === lines.length - 1 && line.includes(" → ");
        return isChangeLine ? { changes: parseChanges(line) } : { text: line };
      });
    },
    handleClose() {
      this.dialog = false;
      this.$emit("closeDialog");
    },
    onEscKey(event) {
      // Escape closes the innermost dialog first
      if (event.key !== "Escape" || this.confirmCard) return;
      if (this.$refs.itemEdit && this.$refs.itemEdit.dialog) return;
      if (this.$refs.addComment && this.$refs.addComment.dialog) return;
      this.handleClose();
    },
    async routeLeave() {
      this.$router.go(-1);
    },
    handleError(message) {
      this.errorMessage = message;
    },
    handleSuccess(message) {
      this.successMessage = message;
    },
    clearMessage() {
      this.errorMessage = "";
      this.successMessage = "";
    },
    showConfirmCard(action) {
      this.confirmCard = true;
      this.pendingAction = action;
      this.confirmTitle = "Marcați demersul ca îndeplinit?";
    },
    confirmAction() {
      this.confirmCard = false;
      if (this.pendingAction) {
        this[this.pendingAction]();
      }
    },
    cancelConfirmCard() {
      this.confirmCard = false;
      this.pendingAction = null;
    },
  },
  computed: {
    ...mapGetters(["getUser"]),
    isEngineerRole() {
      return ["inginer", "inginer-pc"].includes(this.getUser.role);
    },
    statusId() {
      if (this.details) return this.details.statusId;
      return this.item ? this.item.statusId : null;
    },
    doneStep() {
      return this.steps.find((step) => step.id === DONE) || {};
    },
    canFinish() {
      return this.doneStep.id === DONE;
    },
    priceError() {
      return Number.isNaN(parsePrice(this.price)) ? PRICE_ERROR : "";
    },
    canEditPrice() {
      const done = this.statusId === DONE || (this.item && this.item.status === "Îndeplinit");
      return (this.isEngineerRole || this.getUser.role === "admin") && done;
    },
    isCancelled() {
      return this.statusId === STAGES_005.CANCELLED;
    },
    isCorrected() {
      return this.statusId === STAGES_005.CORRECTED;
    },
    cancelStep() {
      const cancelled = this.timeline.filter((step) => step.statusId === STAGES_005.CANCELLED);
      return cancelled[cancelled.length - 1] || null;
    },
    createsEquipment() {
      if (this.details) return !!this.details.createsEquipment;
      return !!(this.item && this.item.createsEquipment);
    },
    canEditItem() {
      return ITEM_EDITOR_ROLES.includes(this.getUser.role) && !!(this.details && this.details.actions && this.details.actions.editItem);
    },
    // whoever opens a demer may comment on it, except on a cancelled one
    canComment() {
      return !!this.item && !this.isCancelled;
    },
  },
  mounted() {
    if (!this.item) {
      this.routeLeave();
    }
    document.addEventListener("keydown", this.onEscKey);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onEscKey);
  },
  watch: {
    item: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.approach_number = newValue.approach_number;
          this.created_at = newValue.created_at;
          this.status = newValue.status;
          this.user = newValue.user;
          this.userDepartment = newValue.userDepartment;
          this.equipment = newValue.equipment;
          this.model = newValue.model;
          this.registration_number = newValue.registration_number;
          this.equipmentdepartment = newValue.equipmentdepartment;
          this.location = newValue.equipmentLocation;
          this.landline = newValue.landline;
          this.telephone =
            newValue.telephone !== null && newValue.telephone !== ""
              ? newValue.telephone
              : "Nu există";
          this.price = newValue.price;
          this.cause = newValue.cause;
          this.loadRequest();
        }
      },
    },
  },
};
</script>

<style scoped>
.hist {
  height: 100%;
}
.fdlg__form.hist__form {
  height: 100%;
  max-height: none;
}
/* head and foot run edge to edge, but their content lines up with the 1100px body column */
.hist .fdlg__head,
.hist .fdlg__foot {
  padding-left: max(24px, calc((100% - 1100px) / 2));
  padding-right: max(24px, calc((100% - 1100px) / 2));
}
.hist__inner {
  max-width: 1100px;
  margin: 0 auto;
}
.summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 12px 20px;
  margin: 0 0 12px;
}
.summary dt {
  color: var(--ink-3);
  font-size: 0.92rem;
  margin-bottom: 4px;
}
.summary dd {
  margin: 0;
  font-weight: 600;
  word-break: break-word;
}
.facts-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 16px;
}
.facts {
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 8px 14px;
  margin: 0 0 12px;
}
.facts dt {
  color: var(--ink-3);
}
.facts dd {
  margin: 0;
  font-weight: 600;
  word-break: break-word;
}
.notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 16px;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 1.02rem;
}
.notice--brick {
  background: var(--brick-bg);
  color: #7a1f1a;
  border: 2px solid var(--brick);
}
.notice--brick .v-icon {
  color: var(--brick);
}
.notice--sky {
  background: var(--sky-bg);
  color: var(--sky);
}
.notice--sky .v-icon {
  color: var(--sky);
}
.new-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: -4px 0 12px;
  padding: 3px 12px 3px 9px;
  border-radius: 999px;
  background: var(--plum-bg);
  color: var(--plum);
  font-size: 0.88rem;
  font-weight: 700;
}
.new-item .v-icon {
  color: var(--plum);
}
.item-edit {
  margin: 0 0 12px;
}
.item-edit__hint {
  margin: 6px 0 0;
  color: var(--ink-3);
  font-size: 0.88rem;
}
/* the request's road: one step per decision, oldest first */
.road {
  list-style: none;
  margin: 0 0 8px;
  padding: 0;
}
.road__step {
  position: relative;
  display: grid;
  grid-template-columns: 20px 1fr;
  gap: 12px;
  padding: 8px 10px 14px;
  border-radius: 10px;
  color: var(--slate);
}
.road__step:not(:last-child)::before {
  content: "";
  position: absolute;
  left: 19px;
  top: 30px;
  bottom: -8px;
  width: 2px;
  background: var(--line);
}
.road__dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 4px solid #fff;
  box-shadow: 0 0 0 2px currentColor;
  background: currentColor;
  margin-top: 2px;
}
.road__body {
  min-width: 0;
  color: var(--ink);
}
.road__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
}
.road__date {
  color: var(--ink-3);
  font-size: 0.88rem;
  font-variant-numeric: tabular-nums;
}
.road__text {
  margin-top: 4px;
  color: var(--ink-2);
  white-space: pre-line;
  word-break: break-word;
}
.road__changes {
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
}
.road__changes li {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  padding: 2px 0;
  word-break: break-word;
}
.road__changes .v-icon {
  color: var(--ink-3);
}
.road__field {
  font-weight: 700;
}
.road__from {
  color: var(--ink-3);
  text-decoration: line-through;
}
.road__to {
  font-weight: 600;
}
.tone-sky { color: var(--sky); }
.tone-teal { color: var(--teal); }
.tone-amber { color: var(--amber); }
.tone-green { color: var(--green); }
.tone-brick { color: var(--brick); }
.tone-slate { color: var(--slate); }
.road__step.is-corrected {
  color: var(--sky);
  background: var(--sky-bg);
}
.road__step.is-changed {
  color: var(--plum);
  background: var(--plum-bg);
}
.road__step.is-cancelled {
  color: var(--brick);
  background: var(--brick-bg);
}
/* a comment does not move the request: hollow dot, no band */
.road__step.is-comment {
  color: var(--plum);
}
.road__step.is-comment .road__dot {
  background: #fff;
}
.road__step.is-corrected .road__dot,
.road__step.is-changed .road__dot,
.road__step.is-cancelled .road__dot {
  border-color: transparent;
}
.road__photos {
  margin-top: 8px;
}
.road__add {
  margin-left: 10px;
}
.confirm {
  padding: 26px 26px 20px;
}
.confirm__icon .v-icon {
  font-size: 40px;
  color: var(--green);
}
.confirm__title {
  margin: 10px 0 8px;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 1.35rem;
  line-height: 1.3;
  color: var(--ink);
}
.confirm__text {
  margin: 0 0 22px;
  color: var(--ink-2);
}
.confirm__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
@media (max-width: 760px) {
  .facts-grid {
    grid-template-columns: 1fr;
  }
}
</style>
